import React, { useState } from "react";
import Modal from "../../components/common/Modal";
import { calculatePercentage } from "../../utils/helpers";
import { getImageUrl } from "../../utils/imageHelper";

// Helper function to get rank suffix (1st, 2nd, 3rd, etc.)
const getRankSuffix = (rank) => {
  if (rank === 1) return "st";
  if (rank === 2) return "nd";
  if (rank === 3) return "rd";
  return "th";
};

// Helper function to get rank badge color for table
const getRankBadgeColor = (rank) => {
  if (rank === 1) return "bg-gradient-to-br from-yellow-400 to-yellow-600 text-white";
  if (rank === 2) return "bg-gradient-to-br from-gray-300 to-gray-500 text-white";
  if (rank === 3) return "bg-gradient-to-br from-orange-400 to-orange-600 text-white";
  return "bg-gradient-to-br from-blue-500 to-blue-600 text-white";
};

// Desktop Table Row
const LeaderboardTableRow = ({ contestant, index, categoryTotalVotes }) => {
  const rank = index + 1;
  const percentage = calculatePercentage(contestant.voteCount || 0, categoryTotalVotes);
  const [imageError, setImageError] = useState(false);

  return (
    <tr className={`hover:bg-gray-50 transition-colors ${rank <= 3 ? "bg-yellow-50/30" : ""}`}>
      {/* Rank */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div
          className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${getRankBadgeColor(rank)} font-bold text-sm shadow-md`}
        >
          {rank}
          <sup className="text-[8px]">{getRankSuffix(rank)}</sup>
        </div>
      </td>

      {/* Contestant */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          {contestant.photo && !imageError ? (
            <img
              src={getImageUrl(contestant.photo)}
              alt={contestant.name}
              className="w-12 h-12 rounded-full object-cover mr-3 border-2 border-gray-200 shadow-sm"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center mr-3 border-2 border-gray-200 shadow-sm">
              <span className="text-white font-bold text-lg">
                {contestant.name?.charAt(0) || "C"}
              </span>
            </div>
          )}
          <div>
            <div className="text-sm font-semibold text-gray-900">{contestant.name}</div>
            {contestant.contestantCode && (
              <div className="text-xs text-gray-500 mt-0.5">
                Code: #{contestant.contestantCode}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Votes */}
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <span className="text-lg font-bold text-red-600">
          {(contestant.voteCount || 0).toLocaleString()}
        </span>
      </td>

      {/* Percentage */}
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="flex items-center justify-end">
          <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
            <div
              className="bg-gradient-to-r from-red-500 to-red-600 h-2 rounded-full transition-all"
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
          <span className="text-sm font-semibold text-gray-700">{percentage}%</span>
        </div>
      </td>
    </tr>
  );
};

// Mobile Card
const LeaderboardMobileCard = ({ contestant, index, categoryTotalVotes }) => {
  const rank = index + 1;
  const percentage = calculatePercentage(contestant.voteCount || 0, categoryTotalVotes);
  const [imageError, setImageError] = useState(false);

  return (
    <div className={`p-4 ${rank <= 3 ? "bg-yellow-50/50" : "bg-white"}`}>
      <div className="flex items-start gap-3">
        {/* Rank Badge */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full ${getRankBadgeColor(rank)} flex items-center justify-center font-bold text-sm shadow-md`}
        >
          {rank}
          <sup className="text-[8px]">{getRankSuffix(rank)}</sup>
        </div>

        {/* Photo */}
        {contestant.photo && !imageError ? (
          <img
            src={getImageUrl(contestant.photo)}
            alt={contestant.name}
            className="flex-shrink-0 w-16 h-16 rounded-lg object-cover border-2 border-gray-200 shadow-sm"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center border-2 border-gray-200 shadow-sm">
            <span className="text-white font-bold text-xl">
              {contestant.name?.charAt(0) || "C"}
            </span>
          </div>
        )}

        {/* Details */}
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-gray-900 text-sm mb-1">{contestant.name}</h4>
          {contestant.contestantCode && (
            <p className="text-xs text-gray-600 mb-2">Code: #{contestant.contestantCode}</p>
          )}
          <div className="flex items-center justify-between mb-2">
            <span className="text-lg font-bold text-red-600">
              {(contestant.voteCount || 0).toLocaleString()} votes
            </span>
            <span className="text-sm font-semibold text-gray-700">{percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-red-500 to-red-600 h-2 rounded-full transition-all"
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ✅ Main Leaderboard Category Modal
const LeaderboardCategoryModal = ({ isOpen, onClose, category }) => {
  if (!category) return null;

  const categoryTotalVotes = category.contestants.reduce(
    (sum, c) => sum + (c.voteCount || 0),
    0
  );

  const sortedContestants = [...category.contestants].sort(
    (a, b) => (b.voteCount || 0) - (a.voteCount || 0)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${category.name} — Leaderboard`}
      size="lg"
    >
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {sortedContestants.length} contestant{sortedContestants.length !== 1 ? "s" : ""}
        </p>
        <p className="text-sm text-gray-500">
          Total votes: <span className="font-semibold text-red-600">{categoryTotalVotes.toLocaleString()}</span>
        </p>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Rank</th>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Contestant</th>
              <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Votes</th>
              <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Percentage</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedContestants.map((contestant, index) => (
              <LeaderboardTableRow
                key={`${contestant.id}-${index}`}
                contestant={contestant}
                index={index}
                categoryTotalVotes={categoryTotalVotes}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-gray-200 rounded-lg border border-gray-200 overflow-hidden">
        {sortedContestants.map((contestant, index) => (
          <LeaderboardMobileCard
            key={`${contestant.id}-${index}`}
            contestant={contestant}
            index={index}
            categoryTotalVotes={categoryTotalVotes}
          />
        ))}
      </div>
    </Modal>
  );
};

export default LeaderboardCategoryModal;